import { GDriveFile, GDriveFileWithPath } from 'infrastructure/googleDrive'
import { deleteNode } from '../../TreeActions'
import { UpdateItem } from './types'
import { stringOrNumberSort } from '../../../Helpers'
import { exportCsv } from 'shared/utils'
import { createNote } from '../../TreeHelpers'
import { INode } from 'shared/types/tree'
import { nodesOfTypeWithContent } from '../../../utility/TreeHelpers'
import { NodeType } from 'shared/types/ingestion'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useMemo } from 'react'
import { createIdMap } from '../../../version_mapping/utility'
import { currentTreeIdsAtom, gDriveFilesAtom } from './atoms'
import { useGetTreeById } from '../../../../reducers/trees_hooks'
import { appendNodeToTree, retrieveIds } from '../../TreeRequests'

export function useCurrentTree() {
  const treeIds = useAtomValue(currentTreeIdsAtom)
  return useGetTreeById(treeIds)
}

export function useTreeIdMap() {
  const { tree } = useCurrentTree()
  return useMemo(() => {
    if (!tree) return
    return createIdMap(tree)
  }, [tree])
}

export async function deleteExisting(update: UpdateItem) {
  if (!update.existingNode) return
  const deleteParams = {
    treeId: update.existingNode.tree_id,
    versionId: update.existingNode.version,
    nodeId: update.existingNode.id,
  }
  return deleteNode(deleteParams)
}

function gdriveFileSort({ name: a }: GDriveFile, { name: b }: GDriveFile) {
  const [a1, a2] = a.split(' ')
  const [b1, b2] = b.split(' ')
  if (a1 === b1) return stringOrNumberSort(a2, b2)
  return stringOrNumberSort(a1, b1)
}

function updatesSort(a: UpdateItem, b: UpdateItem) {
  return gdriveFileSort(a.file, b.file)
}

function s3UrlForFile(file: GDriveFileWithPath) {
  return `https://kiddom-media-production.s3.us-east-2.amazonaws.com/UP_Videos/UP+Videos${file.namePath}`
}

export function exportFileData(title: string, files: GDriveFileWithPath[]) {
  const sorted = [...files].sort(gdriveFileSort)
  const content = [
    ['File Name', 'File Url'],
    ...sorted.map((file) => {
      return [file.name, encodeURI(s3UrlForFile(file))]
    }),
  ]

  exportCsv(content, title)
}

export async function doUpdate(update: UpdateItem) {
  const fileName = s3UrlForFile(update.file)
  const textNodeContent = `<p><span><video width="100%" height="400" controls="controls"><source src="${fileName}" type="video/mp4"></video><span class="mce-shim"></span></span></p>`

  const textNode = createNote({
    title: 'UP Video',
    studentFacing: true,
    value: textNodeContent,
  })

  return appendNodeToTree({
    ...retrieveIds(update.activity),
    next_sibling_node_id: update.question.id,
    ...textNode,
  })
}

function activityVideoLinkNode(activity: INode, videoFile: GDriveFile) {
  return nodesOfTypeWithContent({
    tree: activity,
    types: [NodeType.Note],
    content: [videoFile.name],
  }).pop()
}

export function useUpdatesItemList() {
  const { tree } = useCurrentTree()
  const files = useAtomValue(gDriveFilesAtom)

  return useMemo(() => {
    if (!tree) return { updates: [], extraFiles: [] }
    const nodes = getSeriesNodes(tree)

    const { updates, extraFiles } = files.reduce<{
      updates: UpdateItem[]
      extraFiles: GDriveFileWithPath[]
    }>(
      (acc, file) => {
        const seriesKey = getSeriesKey(file.name)
        if (!seriesKey) {
          acc.extraFiles.push(file)
          return acc
        }
        const series = nodes[seriesKey]
        if (!series) {
          acc.extraFiles.push(file)
          return acc
        }
        const activity = getActivityForSeries(series, file.name)
        if (!activity) {
          acc.extraFiles.push(file)
          return acc
        }
        const question = getQuestionForActivity(activity, file.name)
        if (!question) {
          acc.extraFiles.push(file)
          return acc
        }
        acc.updates.push({
          file,
          activity,
          question,
          existingNode: activityVideoLinkNode(activity, file),
        })
        return acc
      },
      { updates: [], extraFiles: [] },
    )
    updates.sort(updatesSort)
    extraFiles.sort(gdriveFileSort)
    return { updates, extraFiles }
  }, [files, tree])
}

function getSeriesNodes(tree: INode) {
  const idMap = createIdMap(tree)
  const nodes = nodesOfTypeWithContent({
    tree,
    types: [NodeType.Activity],
    extraCheck: ({ title }) => {
      return title.includes('Session ') || title.includes(': Check')
    },
  })

  return nodes.reduce<Record<string, INode[]>>((acc, node) => {
    const { node: parentList } = idMap[node.parent_id]
    const { node: parentUnit } = idMap[parentList.parent_id]
    if (!(parentUnit.title in acc)) {
      acc[parentUnit.title] = []
    }
    acc[parentUnit.title].push(node)
    return acc
  }, {})
}

function getSeriesKey(fileName: string) {
  const [typeKey, ...rest] = fileName
  if (!['E', 'S'].includes(typeKey)) return
  const [seriesNum] = rest.join('').split('S')

  if (isNaN(Number(seriesNum))) return

  const title = `Series ${seriesNum}`

  if (typeKey === 'E') return `Intro ${title}`
  return title
}

function getActivityForSeries(series: INode[], fileName: string) {
  if (fileName.includes('Check')) {
    return series.find(({ title }) => title.includes('Check'))
  }

  const [_, session] = fileName.slice(1).split('S')
  const lookingFor = `Session ${session[0]}`
  return series.find(({ title }) => title === lookingFor)
}

function getQuestionForActivity(activity: INode, fileName: string) {
  const questions = nodesOfTypeWithContent({
    tree: activity,
    types: [NodeType.Question],
  })

  if (fileName.includes('SeriesCheck')) return questions[0]

  const pNum = Number(fileName.split('.')[0].split('P')[1])
  if (!isNaN(pNum)) {
    return questions[pNum - 1]
  }
}
